import React, { useCallback, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import { Button } from '@mui/material';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { useReactFlow } from 'reactflow';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import DraggableBox from './DraggableBox';

interface ConnectedTagsProps {
  dataConnected?: any[];
  onDeleteEdge?: (sourceId: string) => void;
  onChange?: (e: { target: { name: string; value: any[] } }) => void;
  getFieldName?: () => string;
  name?: string;
  maxVisible?: number;
  nodeId?: string;
  handleId?: string;
}

const ConnectedTags = ({
  dataConnected = [],
  onDeleteEdge: onDeleteEdgeProp,
  onChange,
  getFieldName,
  name = '',
  maxVisible = 3,
  nodeId,
  handleId,
}: ConnectedTagsProps) => {
  const { getEdges, setNodes: setNodeState } = useReactFlow();
  const { schema, onEdgeDelete, setSchema, setSaving, flowId } = useFlowsStore(
    state => state,
  );
  const { user } = useAuthStore(state => state);
  const edges = getEdges();

  const [showAll, setShowAll] = useState<boolean>(() => {
    const storedShowAll = localStorage.getItem(`showAll-${name}`);
    return storedShowAll === 'true';
  });

  const onDeleteEdge = useCallback(
    (sourceId: string) => {
      if (!nodeId || !handleId) {
        // Fallback to prop if nodeId/handleId not provided
        onDeleteEdgeProp?.(sourceId);
        return;
      }

      // Handle custom items (items with 'custom_' prefix)
      if (sourceId?.includes('custom_')) {
        const filteredData = dataConnected?.filter(
          (item: any) => item.id !== sourceId,
        );
        if (onChange) {
          const fieldName = getFieldName
            ? getFieldName() + 'Data'
            : name + 'Data';
          onChange({
            target: {
              name: fieldName,
              value: filteredData,
            },
          });
        }
        return;
      }

      const sourceIncludesNodesBox = sourceId?.split('$')?.[0];

      if (setNodeState && nodeId) {
        const fieldName = getFieldName
          ? getFieldName() + 'Data'
          : name + 'Data';

        setNodeState((currentNodes: any) => {
          const latestEdges = getEdges();
          const filteredEdges = latestEdges?.filter(
            (edge: any) =>
              edge?.target === nodeId &&
              edge?.targetHandle === handleId &&
              edge?.source === sourceIncludesNodesBox,
          );

          const updatedNodes = currentNodes.map((node: any) => {
            if (node.id === nodeId) {
              const currentData = node.data[fieldName] || [];
              const filteredData = currentData.filter((item: any) => {
                const itemSourceId = item.id || item.sourceId || '';
                const isFromDeletedSource =
                  itemSourceId.includes(sourceId) ||
                  sourceId.includes(itemSourceId) ||
                  itemSourceId === sourceId;

                return !isFromDeletedSource;
              });

              return {
                ...node,
                data: {
                  ...node.data,
                  [fieldName]: filteredData,
                },
              };
            }

            return node;
          });

          if (flowId && filteredEdges && filteredEdges.length > 0) {
            setTimeout(() => {
              nodeDataAutoSaveDynamic({
                setNodeState,
                id: sourceId,
                flowId: flowId,
                changeType: 'delete-edges',
                setSchema,
                setSaving,
                newEdges: latestEdges,
                deleteEdges: filteredEdges,
                objectCallerData: [
                  ...(user?.spaceObjects || []),
                  ...(user?.objects || []),
                ],
              });

              if (onEdgeDelete) {
                onEdgeDelete(filteredEdges, filteredEdges[0]?.id);
              }
            }, 0);
          }

          return updatedNodes;
        });
      }
    },
    [
      nodeId,
      handleId,
      getEdges,
      dataConnected,
      onChange,
      getFieldName,
      name,
      setNodeState,
      flowId,
      setSchema,
      setSaving,
      user?.spaceObjects,
      user?.objects,
      onEdgeDelete,
      onDeleteEdgeProp,
    ],
  );

  const handleShowMore = useCallback(() => {
    setShowAll(true);
    localStorage.setItem(`showAll-${name}`, 'true');
  }, [name]);

  const handleShowLess = useCallback(() => {
    setShowAll(false);
    localStorage.setItem(`showAll-${name}`, 'false');
  }, [name]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;

      if (sourceIndex === destinationIndex) {
        return;
      }

      const newItems = Array.from(dataConnected);
      const [removed] = newItems.splice(sourceIndex, 1);
      newItems.splice(destinationIndex, 0, removed);

      if (onChange) {
        const fieldName = getFieldName
          ? getFieldName() + 'Data'
          : name + 'Data';
        onChange({
          target: {
            name: fieldName,
            value: newItems,
          },
        });
      }
    },
    [dataConnected, onChange, getFieldName, name],
  );

  if (!dataConnected || dataConnected.length === 0) {
    return null;
  }

  const visibleItems = showAll
    ? dataConnected
    : dataConnected.slice(0, maxVisible);

  return (
    <div
      className="nodrag"
      style={{
        display: 'flex',
        rowGap: '13px',
        flexDirection: 'column',
        width: '100%',
        position: 'relative',
      }}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {provided => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="nodrag"
            >
              {visibleItems.map((data: any, index: number) => {
                return (
                  <Draggable key={data.id} draggableId={data.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="nodrag"
                        style={{
                          ...provided.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.5 : 1,
                          marginRight: '18px',
                        }}
                      >
                        <DraggableBox
                          index={index}
                          key={data.id}
                          data={data}
                          onDeleteEdge={onDeleteEdge}
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {dataConnected.length > maxVisible && (
        <Button
          onClick={showAll ? handleShowLess : handleShowMore}
          size="small"
          variant="text"
          sx={{
            gap: '8px',
          }}
        >
          {showAll ? (
            <>
              <CaretUp size={16} />
              Show less
            </>
          ) : (
            <>
              <CaretDown size={16} />
              Show more
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default ConnectedTags;
